import useDialog from 'components/ConfirmDialog/useDialog';
import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { THREE_SITE_PI_ALLOWED } from 'utility/frontendMessage';
import { generateRandomId, produce } from 'utility/general';

const useSiteDelegationData = ({ formId = -1, siteInfoId = -1, workflow = '' }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const serverTableKey = 'piDelegation';
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const dispatch = useDispatch();
    const showDialog = useDialog();
    const [isEdit, setEdit] = useState(false);

    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    // useEffect(() => {
    //     const newRows = cloneDeep(remoteData);

    //     updatePostData?.forEach((item) => {
    //         const index = newRows.findIndex((row) => row.id === item.id);

    //         if (index !== -1) {
    //             newRows[index] = item;
    //         } else {
    //             newRows.push(item);
    //         }
    //     });

    //     setRows(newRows);

    //     // setRows([...remoteData, ...(updatePostData ?? [])]);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [updatePostData]);

    const handleEditClick = (row) => {
        setEdit(true);
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const triggerFormCreate = async (id) => {

        if (rows.length >= 3) {
            return showDialog({
                description: THREE_SITE_PI_ALLOWED,
                disableCancelButton: true,
            });
        }

        setEdit(false);
        setSelectedRow({});
        setOpenPopup(true);
    };

    const onRecordUpdate = async (data) => {
        setRows(
            produce((draft) => {
                data.id = `temp_${generateRandomId()}`;
                draft.push(data);
            }),
        );
    };

    const handleEditAfterCreation = (row) => {
        let postData = updatePostData ?? [];
        row.siteInfo = { id: siteInfoId };

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        // update post data
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const handleCreateAfterCreation = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        data.siteInfo = { id: siteInfoId };

        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const handleDeleteAfterCreation = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const [columns] = useState([
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'title', headerName: 'Title', width: 130, tooltip: 'e.g. Dr/Mr/Mrs/Ms/Prof.' },
        {
            field: 'surname',
            headerName: 'Surname',
            width: 120,
            valueGetter: (params) => params.getValue('surname'),
        },
        {
            field: 'firstName',
            headerName: 'First name',
            width: 120,
            valueGetter: (params) => params.getValue('firstName'),
        },
        { field: 'cluster', headerName: 'Cluster', width: 100 },
        {
            field: 'institution',
            headerName: 'Hospital / Institution',
            // tooltip: (
            //     <div>
            //         <div>- Central IRB workflow: "Site PI's Delegation shall be an employee/appointee of HA"</div>
            //         <div style={{ marginTop: 20 }}>
            //             - Joint CUHK-NTEC CREC workflow: "Site PI's Delegation shall be an employee/appointee/student of
            //             the CUHK or HA NTEC"
            //         </div>
            //         <div style={{ marginTop: 20 }}>
            //             - HKU/HA HKW IRB: "Site PI's Delegation shall be an employee/appointee/student of the HKU or HA
            //             HKWC"
            //         </div>
            //     </div>
            // ),
            width: 120,
        },
        {
            field: 'department',
            headerName: 'Department',
            width: 160,
        },
    ]);

    const getFilteredRow = () => {
        return rows.filter((item) => {
            return item.siteInfo.id === siteInfoId;
        });
    };

    return {
        columns,
        rows: getFilteredRow(),
        setRows,
        openPopup,
        setOpenPopup,
        selectedRow,
        triggerFormCreate,
        onRecordUpdate,
        handleEditClick,
        handleDeleteAfterCreation,
        handleCreateAfterCreation,
        handleEditAfterCreation,
        isEdit,
    };
};

export default useSiteDelegationData;
